import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Clock, AlertTriangle, Lock } from 'lucide-react';

const TrialBanner = () => {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated || !user) return null;

  // Calculate days remaining
  const getDaysRemaining = () => {
    if (!user.trial_ends_at) return 0;
    const endDate = new Date(user.trial_ends_at);
    const now = new Date();
    const diff = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  };

  const daysRemaining = getDaysRemaining();

  // Don't show banner for paid users
  if (user.tier !== 'freemium') return null;

  // Trial expired
  if (user.trial_expired || user.is_read_only) {
    return (
      <div className="bg-red-500/20 border-b border-red-500/30 py-2 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-red-400" />
            <span className="text-red-400 text-sm font-medium">
              Your trial has expired. You're in read-only mode.
            </span>
          </div>
          <Link
            to="/pricing"
            className="bg-red-500 hover:bg-red-600 text-white px-4 py-1 rounded-lg text-sm font-medium"
          >
            Upgrade Now
          </Link>
        </div>
      </div>
    );
  }

  // Less than 3 days remaining
  if (daysRemaining <= 3) {
    return (
      <div className="bg-yellow-500/20 border-b border-yellow-500/30 py-2 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-yellow-400" />
            <span className="text-yellow-400 text-sm font-medium">
              {daysRemaining === 0 ? 'Your trial expires today!' : `Only ${daysRemaining} day${daysRemaining > 1 ? 's' : ''} left in your trial`}
            </span>
          </div>
          <Link
            to="/pricing"
            className="bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-1 rounded-lg text-sm font-medium"
          >
            Upgrade Now
          </Link>
        </div>
      </div>
    );
  }

  // Normal trial banner
  return (
    <div className="bg-[#2563EB]/20 border-b border-[#2563EB]/30 py-2 px-4">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-[#2563EB]" />
          <span className="text-[#94A3B8] text-sm">
            <span className="text-[#F8FAFC] font-medium">{daysRemaining} days</span> remaining in your free trial
          </span>
        </div>
        <Link
          to="/pricing"
          className="text-[#2563EB] hover:underline text-sm font-medium"
        >
          View Plans
        </Link>
      </div>
    </div>
  );
};

export default TrialBanner;